import React, { useContext } from 'react';
import _merge from 'lodash/merge';
import { getContext } from 'common';
import { ListItemBasicContext } from './ListItemBasic';
import { ListItemCheckedContext } from './ListItemChecked';
import { ListItemCascadingContext } from './ListItemCascading';
import { CascadingListContext } from './CascadingList';
import { ListContext } from './List';

import Styled from './styles';

export interface ListItemTextProps extends React.ComponentPropsWithRef<'div'> {
  /**  The children, i.e. text to place in the List. */
  children: React.ReactNode;
  /** Display the text as disabled. */
  disabled?: boolean;
  /**
   * To override the theme internally.
   * @ignore
   */
  $theme?: any;
  /**
   * To override the direction internally.
   * @ignore
   */
  rtl?: any;
}

/** Component to add and style a text string in a list item. All other props will be provided to the root element. */
const ListItemText = React.forwardRef<HTMLElement, ListItemTextProps>((props: ListItemTextProps, ref) => getContext(({ RTL }) => {
  const {
    $theme,
    children,
    disabled,
    ...otherProps
  } = props;

  const listContext = useContext(ListContext);
  const cascadingListContext = useContext(CascadingListContext);
  const listItemBasicContext = useContext(ListItemBasicContext);
  const listItemCheckedContext = useContext(ListItemCheckedContext);
  const listItemCascadingContext = useContext(ListItemCascadingContext);
  const isDisabled = listItemBasicContext.disabled || listItemCheckedContext.disabled || listItemCascadingContext.disabled || disabled;
  const hasHoverEffect = listItemBasicContext.hoverEffect && listItemCheckedContext.hoverEffect && listItemCascadingContext.hoverEffect;

  const resolvedTheme = _merge({}, listContext.theme, cascadingListContext.theme, $theme);
  return (
    <Styled.ListItemText
      ref={ref}
      RTL={RTL}
      $theme={resolvedTheme}
      disabled={isDisabled}
      hoverEffect={hasHoverEffect}
      $truncateListText={listContext.truncateListText}
      {...otherProps}
    >
      {children}
    </Styled.ListItemText>
  );
}));

ListItemText.displayName = 'ListItemText';

ListItemText.defaultProps = {
  disabled: false,
  $theme: {},
  rtl: undefined
};

export default ListItemText;
